// components/layout/CustomerHeader.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, MapPin, Package, Home, Store, Truck, User, ShoppingCart, Settings, Info, LogOut, Search, Store as StoreIcon, Award, ChevronDown, LayoutDashboard } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SearchBar from '../common/SearchBar';
import { useSearch } from '../../hooks/useSearch';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';

const CustomerHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const { cart } = useCart();
  const { categories, performSearch } = useSearch();
  const [selectedCategory, setSelectedCategory] = useState('');

  // Check if user is already a vendor
  const isVendor = user?.groups?.some(group => group.name === 'vendor') || false;
  const hasPendingApplication = user?.vendor_application_status === 'pending';
  const isActiveVendor = isVendor && user?.vendor_profile?.is_active !== false;
  // Role switcher
  const [currentRole, setCurrentRole] = useState<'customer' | 'vendor'>(
    location.pathname.startsWith('/vendor') ? 'vendor' : 'customer'
  );

  // Handle scroll visibility with RAF for smoothness
  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          const shouldShow = currentScrollY < 100 ||
            currentScrollY < lastScrollY.current ||
            isMenuOpen;

          setIsVisible(shouldShow);
          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      setIsVisible(true);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q');
    const category = params.get('category');
    if (category) {
      setSelectedCategory(category);
    }
    // Update role based on path
    setCurrentRole(location.pathname.startsWith('/vendor') ? 'vendor' : 'customer');
  }, [location]);

  const handleProtectedClick = (path: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      navigate('/login', { state: { from: path } });
    } else {
      navigate(path);
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleRoleSwitch = (role: 'customer' | 'vendor') => {
    setCurrentRole(role);
    setShowRoleDropdown(false);
    if (role === 'vendor') {
      navigate('/vendor/dashboard');
    } else {
      navigate('/');
    }
  };

  // Get vendor menu label
  const getVendorMenuLabel = () => {
    if (isActiveVendor) return 'Vendor Dashboard';
    if (hasPendingApplication) return 'Vendor Application Pending';
    return 'Become a Vendor';
  };

  const getVendorIcon = () => {
    if (isActiveVendor) return <StoreIcon className="h-5 w-5" />;
    if (hasPendingApplication) return <Award className="h-5 w-5 text-yellow-500" />;
    return <StoreIcon className="h-5 w-5" />;
  };

  const getVendorLink = () => {
    if (isActiveVendor) return '/vendor/dashboard';
    if (hasPendingApplication) return '/vendor/application-status';
    return '/vendor/apply';
  };

  return (
    <>
      {/* Fixed header */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 transition-[top] duration-200 ease-out ${isVisible ? 'top-0' : '-top-full'
          }`}
        style={{
          top: isVisible ? 0 : '-100%',
        }}
      >
        {/* Top announcement bar - desktop only */}
        <div className="hidden md:flex items-center justify-center h-8 bg-gradient-to-r from-blue-600 to-blue-800 text-white text-sm px-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>Shop online and enjoy home delivery • Fast shipping nationwide</span>
            <Truck className="h-4 w-4" />
          </div>
        </div>

        {/* Main header */}
        <div className={`px-4 ${showMobileSearch ? 'py-2' : 'py-3'}`}>
          {showMobileSearch ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMobileSearch(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-700" />
              </button>
              <SearchBar
                variant="mobile"
                onClose={() => setShowMobileSearch(false)}
                autoFocus
                className="flex-1"
              />
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4">
              {/* Logo and mobile menu */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors md:hidden"
                  aria-label="Toggle menu"
                >
                  {isMenuOpen ? (
                    <X className="h-5 w-5 text-gray-700" />
                  ) : (
                    <Menu className="h-5 w-5 text-gray-700" />
                  )}
                </button>

                <Link to="/" className="flex items-center gap-2 group">
                  <div className="p-2 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl group-hover:scale-105 transition-transform">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div className="hidden sm:block">
                    <div className="font-bold text-gray-900 text-lg leading-tight">Wright Tech</div>
                    <div className="text-xs text-gray-500">Premium Electronics</div>
                  </div>
                </Link>
              </div>

              {/* Desktop navigation - center */}
              <nav className="hidden md:flex items-center gap-1 mx-auto">
                <Link
                  to="/"
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                >
                  <Home className="h-4 w-4" />
                  Home
                </Link>
                <Link
                  to="/products"
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                >
                  <Store className="h-4 w-4" />
                  Shop All
                </Link>
                <button
                  onClick={(e) => handleProtectedClick('/orders', e)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                >
                  <Truck className="h-4 w-4" />
                  My Orders
                </button>
              </nav>

              {/* Desktop search bar */}
              <div className="hidden md:flex flex-1 max-w-2xl mx-8">
                <SearchBar
                  variant="desktop"
                  showCategoryFilter={true}
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                  className="w-full"
                />
              </div>

              {/* Role Switcher - Desktop */}
              {isActiveVendor && (
                <div className="hidden lg:block relative">
                  <button
                    onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
                  >
                    {currentRole === 'customer' ? (
                      <>
                        <User className="h-4 w-4" />
                        Customer
                      </>
                    ) : (
                      <>
                        <Store className="h-4 w-4" />
                        Vendor
                      </>
                    )}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {showRoleDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50">
                      <button
                        onClick={() => handleRoleSwitch('customer')}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${currentRole === 'customer' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                      >
                        <User className="h-4 w-4" />
                        Customer View
                      </button>
                      <button
                        onClick={() => handleRoleSwitch('vendor')}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${currentRole === 'vendor' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        Vendor Dashboard
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={() => navigate('/settings')}
                        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Settings className="h-4 w-4" />
                        Settings
                      </button>
                      <button
                        onClick={() => { logout(); setShowRoleDropdown(false); }}
                        className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 rounded-b-xl"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Vendor entry - desktop only (for non-vendors) */}
              {!isActiveVendor && user && (
                <button
                  onClick={() => {
                    if (hasPendingApplication) {
                      navigate('/vendor/application-status');
                    } else {
                      navigate('/vendor/apply');
                    }
                  }}
                  className={`hidden lg:flex p-2.5 rounded-xl transition-colors items-center gap-2 text-sm font-medium ${hasPendingApplication
                      ? 'text-yellow-600 hover:bg-yellow-50'
                      : 'text-blue-600 hover:bg-blue-50'
                    }`}
                  aria-label="Vendor"
                >
                  {hasPendingApplication ? (
                    <Award className="h-5 w-5" />
                  ) : (
                    <StoreIcon className="h-5 w-5" />
                  )}
                  <span className="hidden xl:inline">
                    {hasPendingApplication ? 'Pending' : 'Sell'}
                  </span>
                </button>
              )}

              {/* User actions */}
              <div className="flex items-center gap-2">
                {/* Mobile search button */}
                <button
                  onClick={() => setShowMobileSearch(true)}
                  className="md:hidden p-2.5 hover:bg-gray-100 rounded-xl transition-colors"
                  aria-label="Search"
                >
                  <Search className="h-5 w-5 text-gray-700" />
                </button>

                {/* User profile */}
                <button
                  onClick={(e) => handleProtectedClick('/profile', e)}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors relative group"
                  aria-label={user ? 'My account' : 'Sign in'}
                >
                  <User className="h-5 w-5 text-gray-700" />
                  {user && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></div>
                  )}
                </button>

                {/* Cart */}
                <button
                  onClick={() => navigate('/cart')}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors relative"
                  aria-label="Shopping cart"
                >
                  <ShoppingCart className="h-5 w-5 text-gray-700" />
                  {cart.items.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs flex items-center justify-center font-bold shadow">
                      {cart.items.length > 99 ? '99+' : cart.items.length}
                    </span>
                  )}
                </button>

                {/* Settings - desktop only */}
                <button
                  onClick={(e) => handleProtectedClick('/settings', e)}
                  className="hidden lg:flex p-2.5 hover:bg-gray-100 rounded-xl transition-colors items-center gap-2 text-sm font-medium text-gray-700"
                  aria-label="Settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Spacer */}
      <div
        className="w-full transition-[height] duration-200 ease-in-out"
        style={{
          height: isVisible
            ? (showMobileSearch
              ? '60px'
              : (window.innerWidth >= 768 ? '88px' : '72px'))
            : '0px'
        }}
      />

      {/* Mobile menu */}
      {isMenuOpen && !showMobileSearch && (
        <div className="md:hidden fixed inset-0 z-30 pt-[72px]">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="absolute top-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg max-h-[calc(100vh-72px)] overflow-y-auto animate-slideDown">
            <div className="px-4 py-3">
              <div className="space-y-1">
                <Link
                  to="/"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Home className="h-5 w-5" />
                  <span className="font-medium">Home</span>
                </Link>

                <Link
                  to="/products"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Store className="h-5 w-5" />
                  <span className="font-medium">Shop All</span>
                </Link>

                <button
                  onClick={(e) => {
                    handleProtectedClick('/orders', e);
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-left"
                >
                  <Truck className="h-5 w-5" />
                  <span className="font-medium">My Orders</span>
                </button>

                {/* Vendor Section - Only show if user is logged in */}
                {user && (
                  <div className="relative">
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                    <Link
                      to={getVendorLink()}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors mt-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {getVendorIcon()}
                      <span className="font-medium">{getVendorMenuLabel()}</span>
                      {hasPendingApplication && (
                        <span className="ml-auto text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                          Pending
                        </span>
                      )}
                      {isActiveVendor && (
                        <span className="ml-auto text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </Link>
                  </div>
                )}

                <button
                  onClick={(e) => {
                    handleProtectedClick('/profile', e);
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-left"
                >
                  <User className="h-5 w-5" />
                  <span className="font-medium">{user ? 'My Account' : 'Sign In'}</span>
                </button>

                <button
                  onClick={(e) => {
                    handleProtectedClick('/settings', e);
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-left"
                >
                  <Settings className="h-5 w-5" />
                  <span className="font-medium">Settings</span>
                </button>

                <Link
                  to="/about"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Info className="h-5 w-5" />
                  <span className="font-medium">About Us</span>
                </Link>

                {/* User status */}
                {user && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900">Signed in as</div>
                      <div className="text-sm text-gray-600 truncate">{user.email}</div>
                    </div>
                    <button
                      onClick={() => {
                        logout();
                        setIsMenuOpen(false);
                        navigate('/');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors mt-2"
                    >
                      <LogOut className="h-5 w-5" />
                      <span className="font-medium">Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerHeader;